/**
 * 订单绩效模块 — 付费功能开关
 *
 * 构建时：REACT_APP_ORDER_PERFORMANCE_MODULE=1 为总开关；=0 强制关闭。
 * 未设置总开关时，若任一 REACT_APP_ORDER_PERFORMANCE_* 子开关为 1，视为已开通。
 *
 * 运行时：应用启动后请求 GET /api/config/order-performance-features，与构建配置取交集（二者均须为 true 才开启）。
 */

import { hasPermission, PERM } from '../utils/accessPermissions';

const isOn = (value) => value === '1' || value === 'true';

const readBuildEnv = (name) => isOn(process.env[name]);

const LEGACY_MODULE_ENVS = [
	'REACT_APP_ORDER_PERFORMANCE_ORDER_LINE_GIFT',
	'REACT_APP_ORDER_PERFORMANCE_ORDER_DETAIL_UI',
	'REACT_APP_ORDER_PERFORMANCE_RULES_MANAGEMENT',
	'REACT_APP_ORDER_PERFORMANCE_BULK_REMATCH',
];

function isLegacyBuildModuleEnabled() {
	return LEGACY_MODULE_ENVS.some(readBuildEnv);
}

function isBuildModuleEnabled() {
	const master = process.env.REACT_APP_ORDER_PERFORMANCE_MODULE;
	if (master === '0' || master === 'false') return false;
	if (isOn(master)) return true;
	return isLegacyBuildModuleEnabled();
}

const SUB_BUILD_ENVS = [
	'REACT_APP_ORDER_PERFORMANCE_ORDER_LINE_GIFT',
	'REACT_APP_ORDER_PERFORMANCE_ORDER_DETAIL_UI',
	'REACT_APP_ORDER_PERFORMANCE_RULES_MANAGEMENT',
	'REACT_APP_ORDER_PERFORMANCE_BULK_REMATCH',
];

function isMasterExplicitlySet() {
	const m = process.env.REACT_APP_ORDER_PERFORMANCE_MODULE;
	return m === '1' || m === '0' || m === 'true' || m === 'false';
}

function anySubExplicitlySet() {
	return SUB_BUILD_ENVS.some(readBuildEnv);
}

function buildSubEnabled(envName) {
	if (!isBuildModuleEnabled()) return false;
	if (readBuildEnv(envName)) return true;
	if (!isMasterExplicitlySet() && isLegacyBuildModuleEnabled()) return true;
	if (isOn(process.env.REACT_APP_ORDER_PERFORMANCE_MODULE) && !anySubExplicitlySet()) return true;
	return false;
}

const DEFAULT_LICENSE = {
	valid: true,
	code: 'UNKNOWN',
	message: '',
	enforce: false,
	customer: null,
	expiresAt: null,
	daysRemaining: null,
	bypass: false,
};

const DEFAULT_FEATURES = {
	module: false,
	license: DEFAULT_LICENSE,
	matchOnCreate: false,
	orderLineGiftToggle: false,
	orderDetailUi: false,
	rulesManagement: false,
	bulkRematch: false,
};

/** 仅构建时配置（未拉取 API 前） */
export function getOrderPerformanceFeaturesBuildTime() {
	const module = isBuildModuleEnabled();
	return {
		...DEFAULT_FEATURES,
		module,
		matchOnCreate: false,
		orderLineGiftToggle: buildSubEnabled('REACT_APP_ORDER_PERFORMANCE_ORDER_LINE_GIFT'),
		orderDetailUi: buildSubEnabled('REACT_APP_ORDER_PERFORMANCE_ORDER_DETAIL_UI'),
		rulesManagement: buildSubEnabled('REACT_APP_ORDER_PERFORMANCE_RULES_MANAGEMENT'),
		bulkRematch: buildSubEnabled('REACT_APP_ORDER_PERFORMANCE_BULK_REMATCH'),
	};
}

let runtimeFromApi = null;
let runtimeViewerCapabilities = null;

const DEFAULT_VIEWER_CAPABILITIES = {
	canAccessFinalCostVerificationDashboard: false,
	canViewPerformanceSettlement: false,
	canAccessRulesManagement: false,
};

function canAccessFinalCostVerificationDashboardByAccess(user) {
	return hasPermission(user, PERM.DASHBOARD_ORDER_FINAL_COST_VERIFICATION);
}

function canViewPerformanceSettlementByAccess(user) {
	return hasPermission(user, PERM.ORDER_PERFORMANCE_SETTLEMENT_VIEW);
}

function canAccessRulesManagementByAccess(user) {
	return hasPermission(user, PERM.PERFORMANCE_QUERY) && hasPermission(user, PERM.PERFORMANCE_UPDATE);
}

export function resolveOrderViewerCapabilities(user) {
	if (runtimeViewerCapabilities) {
		return runtimeViewerCapabilities;
	}
	if (!user) {
		return DEFAULT_VIEWER_CAPABILITIES;
	}
	return {
		canAccessFinalCostVerificationDashboard: canAccessFinalCostVerificationDashboardByAccess(user),
		canViewPerformanceSettlement: canViewPerformanceSettlementByAccess(user),
		canAccessRulesManagement: canAccessRulesManagementByAccess(user) && getOrderPerformanceFeatures().rulesManagement,
	};
}

const intersect = (build, api) => ({
	module: Boolean(build.module && api.module),
	license: api.license && typeof api.license === 'object' ? api.license : DEFAULT_LICENSE,
	matchOnCreate: Boolean(build.module && api.matchOnCreate),
	orderLineGiftToggle: Boolean(build.module && api.orderLineGiftToggle),
	orderDetailUi: Boolean(build.module && api.orderDetailUi),
	rulesManagement: Boolean(build.module && api.rulesManagement),
	bulkRematch: Boolean(build.module && api.bulkRematch),
});

/**
 * @param {Partial<typeof DEFAULT_FEATURES>} apiPayload GET /api/config/order-performance-features 响应
 */
export function setOrderPerformanceFeaturesFromApi(apiPayload) {
	if (!apiPayload || typeof apiPayload !== 'object') return;
	runtimeFromApi = intersect(getOrderPerformanceFeaturesBuildTime(), apiPayload);
	runtimeViewerCapabilities =
		apiPayload.viewerCapabilities && typeof apiPayload.viewerCapabilities === 'object'
			? {
					canAccessFinalCostVerificationDashboard: Boolean(apiPayload.viewerCapabilities.canAccessFinalCostVerificationDashboard),
					canViewPerformanceSettlement: Boolean(apiPayload.viewerCapabilities.canViewPerformanceSettlement),
					canAccessRulesManagement: Boolean(apiPayload.viewerCapabilities.canAccessRulesManagement),
				}
			: null;
}

/** 仪表盘/详情权限（GET /api/config/order-performance-features 登录态下附带） */
export function getOrderViewerCapabilities() {
	return runtimeViewerCapabilities ?? DEFAULT_VIEWER_CAPABILITIES;
}

/** 当前生效配置（构建 ∩ 服务端，未拉取 API 前仅构建配置） */
export function getOrderPerformanceFeatures() {
	if (runtimeFromApi) return runtimeFromApi;
	return getOrderPerformanceFeaturesBuildTime();
}

export function isOrderPerformanceModuleEnabled() {
	return getOrderPerformanceFeatures().module;
}

export function isOrderPerformanceOrderLineGiftEnabled() {
	return getOrderPerformanceFeatures().orderLineGiftToggle;
}

export function isOrderPerformanceOrderDetailUiEnabled() {
	return getOrderPerformanceFeatures().orderDetailUi;
}

export function isOrderPerformanceRulesManagementEnabled() {
	return getOrderPerformanceFeatures().rulesManagement;
}

export function isOrderPerformanceBulkRematchEnabled() {
	return getOrderPerformanceFeatures().bulkRematch;
}
